import { useNavigate } from "react-router-dom";
import { MapPin, Truck, Clock, CheckCircle, Smartphone, Download, X, Info, ChevronLeft, ChevronRight } from "lucide-react";
import { Button } from "@/components/ui/button";
import { useState, useEffect, useRef, useCallback } from "react";
import SakuraChatbot from "@/components/SakuraChatbot";
import { SneakersCarousel } from "@/components/SneakersCarousel";
import { PromoOfMonth } from "@/components/PromoOfMonth";

const API = process.env.REACT_APP_BACKEND_URL;

// Image "Qui sommes-nous"
const ABOUT_IMAGE_URL = "https://customer-assets-rejwkqb3.emergentagent.net/job_2865d04f-1753-4ac2-b4d4-74d332d7faa7/artifacts/kdro5m1i_821aad32-2735-4211-8a75-a9400e274c34.png";

// Images Packs économiques
const PACKS_IMAGES = [
  {
    id: 1,
    src: "https://customer-assets-rejwkqb3.emergentagent.net/job_2865d04f-1753-4ac2-b4d4-74d332d7faa7/artifacts/w5hltw3u_dcec5a36-9965-469c-9351-952853d93cee.png",
    alt: "Pack 20 chemises - Détachées, lavées, repassées - 60€ au lieu de 80€"
  },
  {
    id: 2,
    src: "https://customer-assets-rejwkqb3.emergentagent.net/job_2865d04f-1753-4ac2-b4d4-74d332d7faa7/artifacts/pexwcmo5_Offre%20Sakura%20_%2040%20chemises%20%C3%A9l%C3%A9gantes.png",
    alt: "Pack 40 chemises - Détachées, lavées, repassées - 112€ au lieu de 160€"
  },
  {
    id: 3,
    src: "https://customer-assets-rejwkqb3.emergentagent.net/job_2865d04f-1753-4ac2-b4d4-74d332d7faa7/artifacts/jan99783_Pack%20Parure%20de%20Lit%20Sakura.png",
    alt: "Pack Parure de lit - Détaché, lavé, repassé - 10€ au lieu de 13€"
  }
];

const HomePage = () => {
  const navigate = useNavigate();
  const [showInstallBanner, setShowInstallBanner] = useState(false);
  const [deferredPrompt, setDeferredPrompt] = useState(null);
  const [showAboutModal, setShowAboutModal] = useState(false);
  
  // Carrousel Packs économiques
  const [currentPackIndex, setCurrentPackIndex] = useState(0);
  const [touchStart, setTouchStart] = useState(null);
  const [touchEnd, setTouchEnd] = useState(null);
  const carouselRef = useRef(null);
  
  // Promo couettes visible jusqu'au 30 juin 2026
  const promoEndDate = new Date('2026-06-30T23:59:59');
  const showPromo = new Date() <= promoEndDate;
  
  // Bannière permanente Pack Chemises
  const showChemisesBanner = true;
  
  // Promo Robe de Mariée -20% visible jusqu'au 31 août 2026
  const promoRideauxEndDate = new Date('2026-08-31T23:59:59');
  const showPromoRideaux = new Date() <= promoRideauxEndDate;
  
  // Bannière mise à jour créneaux visible jusqu'au 31 août 2026 inclus
  const creneauxUpdateEndDate = new Date('2026-08-31T23:59:59');
  const showCreneauxUpdate = new Date() <= creneauxUpdateEndDate;

  // Swipe handlers pour le carrousel
  const minSwipeDistance = 50;
  
  const onTouchStart = (e) => {
    setTouchEnd(null);
    setTouchStart(e.targetTouches[0].clientX);
  };

  const onTouchMove = (e) => {
    setTouchEnd(e.targetTouches[0].clientX);
  };

  const onTouchEnd = () => {
    if (!touchStart || !touchEnd) return;
    const distance = touchStart - touchEnd;
    const isLeftSwipe = distance > minSwipeDistance;
    const isRightSwipe = distance < -minSwipeDistance;
    
    if (isLeftSwipe && currentPackIndex < PACKS_IMAGES.length - 1) {
      setCurrentPackIndex(prev => prev + 1);
    }
    if (isRightSwipe && currentPackIndex > 0) {
      setCurrentPackIndex(prev => prev - 1);
    }
  };

  const goToPack = (index) => {
    setCurrentPackIndex(index);
  };

  const nextPack = useCallback(() => {
    setCurrentPackIndex(prev => (prev + 1) % PACKS_IMAGES.length);
  }, []);

  const prevPack = useCallback(() => {
    setCurrentPackIndex(prev => (prev - 1 + PACKS_IMAGES.length) % PACKS_IMAGES.length);
  }, []);

  // Track visit on page load
  useEffect(() => {
    const trackVisit = async () => {
      try {
        // Get or create visitor ID
        let visitorId = localStorage.getItem("sakura_visitor_id");
        
        const response = await fetch(`${API}/api/track-visit`, {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify({ page: "home", visitor_id: visitorId })
        });
        
        if (response.ok) {
          const data = await response.json();
          // Store visitor ID for future visits
          if (data.visitor_id && !visitorId) {
            localStorage.setItem("sakura_visitor_id", data.visitor_id);
          }
        }
      } catch (error) {
        // Silent fail - don't interrupt user experience
        console.debug("Visit tracking failed:", error);
      }
    };
    
    trackVisit();
  }, []);

  // PWA Install prompt
  useEffect(() => {
    const handler = (e) => {
      e.preventDefault();
      setDeferredPrompt(e);
      setShowInstallBanner(true);
    };
    
    window.addEventListener('beforeinstallprompt', handler);
    
    // Check if on mobile
    const isMobile = /iPhone|iPad|iPod|Android/i.test(navigator.userAgent);
    if (isMobile) {
      setShowInstallBanner(true);
    }
    
    return () => window.removeEventListener('beforeinstallprompt', handler);
  }, []);

  const handleInstallClick = async () => {
    if (deferredPrompt) {
      deferredPrompt.prompt();
      const { outcome } = await deferredPrompt.userChoice;
      if (outcome === 'accepted') {
        setShowInstallBanner(false);
      }
      setDeferredPrompt(null);
    } else {
      // For iOS, show instructions
      alert("Pour installer l'app :\n\n📱 iPhone : Appuie sur le bouton Partager puis 'Sur l'écran d'accueil'\n\n📱 Android : Appuie sur les 3 points puis 'Installer l'application'");
    }
  };

  const zones = [
    {
      id: "lyon",
      name: "Lyon",
      description: "Lyon 3ᵉ, Lyon 6ᵉ et alentours",
      image: "https://customer-assets.emergentagent.com/job_pressingexpress/artifacts/ntrvnewe_photo.1515687.jpg",
    },
    {
      id: "saint-rambert",
      name: "Saint-Rambert-d'Albon",
      description: "Saint-Rambert-d'Albon et alentours",
      image: "https://customer-assets.emergentagent.com/job_pressingexpress/artifacts/a73z87cj_Visuel-video-e1768213238750.png",
    },
    {
      id: "francheville",
      name: "Francheville",
      description: "Francheville, Écully, Tassin et alentours",
      image: "https://customer-assets-rejwkqb3.emergentagent.net/job_2865d04f-1753-4ac2-b4d4-74d332d7faa7/artifacts/a39zem13_55029530124_9f4692e039_b.jpg",
    },
  ];

  const features = [
    {
      icon: Clock,
      title: "Délai standard : 3 jours",
      description: "Ex : Ramassage lundi → Livraison jeudi",
    },
    {
      icon: Truck,
      title: "Service express 48h",
      description: "+30% sur votre facture (selon disponibilité). Minimum 48h entre ramassage et livraison.",
    },
    {
      icon: CheckCircle,
      title: "Service de qualité",
      description: "Pressing professionnel à domicile",
    },
  ];

  return (
    <div className="min-h-screen">
      {/* Navigation */}
      <nav className="glass-nav fixed top-0 left-0 right-0 z-50">
        <div className="max-w-7xl mx-auto px-6 py-2 flex justify-between items-center">
          <div className="flex items-center">
            <img 
              src="https://customer-assets.emergentagent.com/job_pressingexpress/artifacts/d4qg5zqd_Capture%20d%E2%80%99%C3%A9cran%202025-01-06%20102414.png" 
              alt="Pressing Sakura" 
              className="h-14 w-auto"
            />
          </div>
          <div className="flex items-center gap-2 sm:gap-3">
          <PromoOfMonth />
          <Button
            variant="outline"
            onClick={() => navigate("/connexion")}
            className="text-slate-700 hover:text-primary hover:border-primary"
            data-testid="mon-compte-btn"
          >
            <svg className="w-4 h-4 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M16 7a4 4 0 11-8 0 4 4 0 018 0zM12 14a7 7 0 00-7 7h14a7 7 0 00-7-7z" />
            </svg>
            Mon compte
          </Button>
          </div>
        </div>
      </nav>

      {/* PWA Install Banner - Mobile only */}
      {showInstallBanner && (
        <div className="fixed bottom-0 left-0 right-0 z-40 p-4 bg-gradient-to-r from-pink-500 to-purple-600 text-white shadow-lg" style={{ position: 'fixed', transform: 'translateZ(0)' }}>
          <div className="max-w-4xl mx-auto flex items-center justify-between gap-4">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 bg-white/20 rounded-full flex items-center justify-center">
                <Smartphone className="w-5 h-5" />
              </div>
              <div>
                <p className="font-semibold text-sm">Télécharge l'application</p>
                <p className="text-xs text-pink-100">Accède à ton compte en un clic !</p>
              </div>
            </div>
            <div className="flex gap-2">
              <Button
                size="sm"
                onClick={handleInstallClick}
                className="bg-white text-pink-600 hover:bg-pink-50"
              >
                <Download className="w-4 h-4 mr-1" />
                Installer
              </Button>
              <button 
                onClick={() => setShowInstallBanner(false)}
                className="text-white/70 hover:text-white text-xl px-2"
              >
                ×
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Bannière mise à jour créneaux - jusqu'au 31 août 2026 */}
      {showCreneauxUpdate && (
        <section className="pt-20 pb-2 px-4 bg-gradient-to-b from-blue-50 to-pink-50">
          <div className="max-w-4xl mx-auto">
            <div className="relative rounded-2xl overflow-hidden shadow-xl">
              <img 
                src="https://customer-assets-rejwkqb3.emergentagent.net/job_2865d04f-1753-4ac2-b4d4-74d332d7faa7/artifacts/49t0ck1j_ChatGPT%20Image%2023%20ao%C3%BBt%202026%2C%2019_01_33.png"
                alt="Importante mise à jour des créneaux - À partir du 1er septembre"
                className="w-full h-auto"
              />
            </div>
          </div>
        </section>
      )}

      {/* Carrousel Packs économiques */}
      <section className={`${showCreneauxUpdate ? 'pt-4' : 'pt-20'} pb-4 px-4 bg-gradient-to-b from-pink-50 to-white`}>
        <div className="max-w-4xl mx-auto">
          <h3 className="text-2xl sm:text-3xl font-bold text-center text-pink-600 mb-6" style={{ fontFamily: 'Outfit, sans-serif' }}>
            🎁 Packs économiques
          </h3>
          <div 
            ref={carouselRef}
            className="relative rounded-2xl overflow-hidden shadow-xl bg-white"
            onTouchStart={onTouchStart}
            onTouchMove={onTouchMove}
            onTouchEnd={onTouchEnd}
          >
            {/* Images du carrousel */}
            <div 
              className="flex transition-transform duration-300 ease-in-out"
              style={{ transform: `translateX(-${currentPackIndex * 100}%)` }}
            >
              {PACKS_IMAGES.map((pack) => (
                <div key={pack.id} className="w-full flex-shrink-0">
                  <img 
                    src={pack.src}
                    alt={pack.alt}
                    className="w-full h-auto"
                    draggable={false}
                  />
                </div>
              ))}
            </div>
            
            {/* Flèches de navigation (desktop) */}
            <button 
              onClick={prevPack}
              className="absolute left-2 top-1/2 -translate-y-1/2 w-10 h-10 bg-white/80 hover:bg-white rounded-full shadow-lg flex items-center justify-center transition-all opacity-0 md:opacity-100 hover:scale-110"
              aria-label="Image précédente"
            >
              <ChevronLeft className="w-6 h-6 text-pink-600" />
            </button>
            <button 
              onClick={nextPack}
              className="absolute right-2 top-1/2 -translate-y-1/2 w-10 h-10 bg-white/80 hover:bg-white rounded-full shadow-lg flex items-center justify-center transition-all opacity-0 md:opacity-100 hover:scale-110"
              aria-label="Image suivante"
            >
              <ChevronRight className="w-6 h-6 text-pink-600" />
            </button>
            
            {/* Indicateur swipe (mobile) */}
            <div className="absolute bottom-20 left-1/2 -translate-x-1/2 md:hidden animate-pulse">
              <div className="flex items-center gap-1 bg-black/60 text-white text-xs px-3 py-1.5 rounded-full">
                <ChevronLeft className="w-4 h-4" />
                <span>Glissez</span>
                <ChevronRight className="w-4 h-4" />
              </div>
            </div>
            
            {/* Points indicateurs */}
            <div className="absolute bottom-4 left-1/2 -translate-x-1/2 flex gap-2">
              {PACKS_IMAGES.map((_, index) => (
                <button
                  key={index}
                  onClick={() => goToPack(index)}
                  className={`w-3 h-3 rounded-full transition-all ${
                    currentPackIndex === index 
                      ? 'bg-pink-600 scale-110' 
                      : 'bg-white/70 hover:bg-white'
                  }`}
                  aria-label={`Aller au pack ${index + 1}`}
                />
              ))}
            </div>
          </div>
        </div>
      </section>

      <SneakersCarousel />

      {/* Hero Section */}
      <section className={`hero-gradient ${showChemisesBanner ? 'pt-12' : 'pt-32'} pb-20 px-6`}>
        <div className="max-w-7xl mx-auto">
          <div className="max-w-3xl">
            <p className="text-primary font-medium tracking-[0.2em] uppercase text-sm mb-4">
              Service de pressing
            </p>
            <h1 
              className="text-4xl sm:text-5xl lg:text-6xl font-bold text-slate-900 tracking-tight mb-6"
              style={{ fontFamily: 'Outfit, sans-serif' }}
            >
              Réservez votre créneau de ramassage et de livraison
            </h1>
            <p className="text-lg text-slate-600 mb-8 max-w-xl">
              Simple, rapide et pratique. Sélectionnez votre zone et choisissez le créneau qui vous convient le mieux.
            </p>
            <div className="flex flex-wrap items-center gap-4 mb-6">
              <a 
                href="https://www.pressing-sakura.fr/tarifs"
                target="_blank"
                rel="noopener noreferrer"
                className="inline-flex items-center gap-2 bg-white border-2 border-primary text-primary px-6 py-3 rounded-full font-semibold hover:bg-primary hover:text-white transition-all duration-300 shadow-md hover:shadow-lg"
              >
                <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5H7a2 2 0 00-2 2v12a2 2 0 002 2h10a2 2 0 002-2V7a2 2 0 00-2-2h-2M9 5a2 2 0 002 2h2a2 2 0 002-2M9 5a2 2 0 012-2h2a2 2 0 012 2m-6 9l2 2 4-4" />
                </svg>
                Voir nos tarifs
              </a>
              <button
                onClick={() => setShowAboutModal(true)}
                className="inline-flex items-center gap-2 bg-white border-2 border-purple-500 text-purple-600 px-6 py-3 rounded-full font-semibold hover:bg-purple-500 hover:text-white transition-all duration-300 shadow-md hover:shadow-lg"
              >
                <Info className="w-5 h-5" />
                Qui sommes-nous ?
              </button>
              <a 
                href="tel:0766833051"
                className="inline-flex items-center gap-2 bg-green-500 text-white px-6 py-3 rounded-full font-semibold hover:bg-green-600 transition-all duration-300 shadow-md hover:shadow-lg"
              >
                <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 5a2 2 0 012-2h3.28a1 1 0 01.948.684l1.498 4.493a1 1 0 01-.502 1.21l-2.257 1.13a11.042 11.042 0 005.516 5.516l1.13-2.257a1 1 0 011.21-.502l4.493 1.498a1 1 0 01.684.949V19a2 2 0 01-2 2h-1C9.716 21 3 14.284 3 6V5z" />
                </svg>
                07 66 83 30 51
              </a>
              <a 
                href="mailto:formation-pressing-sakura@hotmail.com"
                className="inline-flex items-center gap-2 bg-blue-500 text-white px-6 py-3 rounded-full font-semibold hover:bg-blue-600 transition-all duration-300 shadow-md hover:shadow-lg"
              >
                <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 8l7.89 5.26a2 2 0 002.22 0L21 8M5 19h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z" />
                </svg>
                Email
              </a>
            </div>
            <p className="text-sm text-slate-500 flex items-center gap-2">
              <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8.228 9c.549-1.165 2.03-2 3.772-2 2.21 0 4 1.343 4 3 0 1.4-1.278 2.575-3.006 2.907-.542.104-.994.54-.994 1.093m0 3h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
              </svg>
              Une question ou un problème pour réserver ? Appelez-nous !
            </p>
          </div>

          {/* Features */}
          <div className="grid grid-cols-1 md:grid-cols-3 gap-6 mt-12">
            {features.map((feature, index) => (
              <div
                key={index}
                className="bg-white/60 backdrop-blur-sm rounded-2xl p-6 border border-slate-100"
              >
                <div className="w-12 h-12 bg-accent rounded-xl flex items-center justify-center mb-4">
                  <feature.icon className="w-6 h-6 text-accent-foreground" />
                </div>
                <h3 className="font-semibold text-slate-900 mb-2" style={{ fontFamily: 'Outfit, sans-serif' }}>
                  {feature.title}
                </h3>
                <p className="text-slate-600 text-sm">{feature.description}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Promo Robe de Mariée -20% - Visible jusqu'au 31 août 2026 */}
      {showPromoRideaux && (
        <section className="py-8 px-4 bg-gradient-to-b from-white to-pink-50">
          <div className="max-w-4xl mx-auto">
            <div className="relative rounded-2xl overflow-hidden shadow-xl">
              <img 
                src="https://customer-assets-rejwkqb3.emergentagent.net/job_2865d04f-1753-4ac2-b4d4-74d332d7faa7/artifacts/h82sp05a_Offre%20sp%C3%A9ciale%20pour%20robe%20de%20mari%C3%A9e.png"
                alt="Offre Spéciale Août -20% sur le nettoyage des robes de mariée - Valable tout le mois d'août"
                className="w-full h-auto"
              />
            </div>
          </div>
        </section>
      )}

      {/* Zone Selection */}
      <section className="py-20 px-6 bg-white">
        <div className="max-w-7xl mx-auto">
          {/* Bannière Nouveau Service de Retouche */}
          <div className="max-w-lg mx-auto mb-10">
            <div className="relative rounded-2xl overflow-hidden shadow-xl">
              <img 
                src="https://customer-assets-rejwkqb3.emergentagent.net/job_2865d04f-1753-4ac2-b4d4-74d332d7faa7/artifacts/b3e9bbc3b17ad83c_image_17e72966.jpg"
                alt="Nouveau service de retouche : confiez-nous vos textiles, votre vêtement réparé, nettoyé, repassé sans bouger de chez vous."
                className="w-full h-auto"
                data-testid="home-promo-retouche"
              />
            </div>
          </div>

          <div className="text-center mb-12">
            <h2 
              className="text-3xl sm:text-4xl font-bold text-slate-900 tracking-tight mb-4"
              style={{ fontFamily: 'Outfit, sans-serif' }}
            >
              Sélectionnez votre zone
            </h2>
            <p className="text-slate-600 max-w-md mx-auto">
              Nous livrons dans trois secteurs. Choisissez celui qui correspond à votre adresse.
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-8 max-w-4xl mx-auto">
            {zones.map((zone) => (
              <button
                key={zone.id}
                onClick={() => navigate(`/booking/${zone.id}`)}
                className="zone-card group text-left"
                data-testid={`zone-selector-${zone.id}`}
              >
                <div className="aspect-video rounded-xl overflow-hidden mb-4 bg-slate-100">
                  <img
                    src={zone.image}
                    alt={zone.name}
                    className="w-full h-full object-cover transition-transform duration-500 group-hover:scale-105"
                  />
                </div>
                <div className="flex items-start gap-3">
                  <div className="w-10 h-10 bg-primary/10 rounded-lg flex items-center justify-center flex-shrink-0">
                    <MapPin className="w-5 h-5 text-primary" />
                  </div>
                  <div>
                    <h3 
                      className="font-semibold text-xl text-slate-900 mb-1"
                      style={{ fontFamily: 'Outfit, sans-serif' }}
                    >
                      {zone.name}
                    </h3>
                    <p className="text-slate-500 text-sm">{zone.description}</p>
                  </div>
                </div>
              </button>
            ))}
          </div>
        </div>
      </section>

      {/* Bannière Jeu Sakura - Fidélité */}
      <section className="py-8 px-4 bg-gradient-to-b from-white to-pink-50">
        <div className="max-w-4xl mx-auto">
          <div 
            className="relative rounded-2xl overflow-hidden shadow-xl cursor-pointer hover:shadow-2xl transition-shadow duration-300"
            onClick={() => navigate("/connexion")}
          >
            <img 
              src="https://customer-assets-rejwkqb3.emergentagent.net/job_2865d04f-1753-4ac2-b4d4-74d332d7faa7/artifacts/lgkprqfj_de6316cf-6c31-4dea-acb6-4e5016fc2784%20%282%29.png"
              alt="Jeu Sakura - Tentez votre chance chaque mois ! Connectez-vous à votre espace client et cliquez sur un pétale pour découvrir votre surprise."
              className="w-full h-auto"
            />
          </div>
          <p className="text-center text-sm text-slate-500 mt-3">
            Cliquez sur la bannière pour accéder à votre espace client 🌸
          </p>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-20 px-6 bg-slate-50">
        <div className="max-w-4xl mx-auto text-center">
          <h2 
            className="text-2xl sm:text-3xl font-bold text-slate-900 tracking-tight mb-4"
            style={{ fontFamily: 'Outfit, sans-serif' }}
          >
            Prêt à réserver ?
          </h2>
          <p className="text-slate-600 mb-8">
            Sélectionnez votre zone ci-dessus pour voir les créneaux disponibles.
          </p>
          <div className="flex flex-wrap gap-4 justify-center">
            <Button
              onClick={() => navigate("/booking/lyon")}
              className="btn-primary"
              data-testid="cta-lyon-btn"
            >
              Réserver à Lyon
            </Button>
            <Button
              onClick={() => navigate("/booking/saint-rambert")}
              variant="outline"
              className="rounded-full px-8 py-3"
              data-testid="cta-saint-rambert-btn"
            >
              Réserver à Saint-Rambert
            </Button>
            <Button
              onClick={() => navigate("/booking/francheville")}
              variant="outline"
              className="rounded-full px-8 py-3"
              data-testid="cta-francheville-btn"
            >
              Réserver à Francheville
            </Button>
          </div>
        </div>
      </section>

      {/* Footer */}
      <footer className="bg-slate-900 text-white py-12 px-6">
        <div className="max-w-7xl mx-auto">
          <div className="flex flex-col md:flex-row justify-between items-center gap-6">
            <div className="flex items-center">
              <img 
                src="https://customer-assets.emergentagent.com/job_pressingexpress/artifacts/d4qg5zqd_Capture%20d%E2%80%99%C3%A9cran%202025-01-06%20102414.png" 
                alt="Pressing Sakura" 
                className="h-12 w-auto bg-white rounded-lg p-1"
              />
            </div>
            <div className="flex items-center gap-6 text-sm">
              <a href="/mentions-legales" className="text-slate-400 hover:text-white transition-colors">
                Mentions légales
              </a>
              <a href="/confidentialite" className="text-slate-400 hover:text-white transition-colors">
                Politique de confidentialité
              </a>
            </div>
            <p className="text-slate-400 text-sm">
              © 2024 Pressing Sakura. Tous droits réservés.
            </p>
          </div>
        </div>
      </footer>

      {/* Sakura Chatbot - Fixed position */}
      <div className="fixed bottom-6 right-6 z-50" style={{ position: 'fixed', transform: 'translateZ(0)' }}>
        <SakuraChatbot />
      </div>

      {/* Modal "Qui sommes-nous" - Fullscreen image */}
      {showAboutModal && (
        <div 
          className="fixed inset-0 z-[100] bg-black/90 flex items-center justify-center p-4"
          onClick={() => setShowAboutModal(false)}
        >
          <button
            onClick={() => setShowAboutModal(false)}
            className="absolute top-4 right-4 text-white bg-black/50 hover:bg-black/70 rounded-full p-2 z-10"
          >
            <X className="w-8 h-8" />
          </button>
          <img 
            src={ABOUT_IMAGE_URL}
            alt="Qui sommes-nous - Formation Pressing Sakura"
            className="max-w-full max-h-full object-contain rounded-lg shadow-2xl"
            onClick={(e) => e.stopPropagation()}
          />
        </div>
      )}
    </div>
  );
};

export default HomePage;
